import React from 'react';
import PropTypes from 'prop-types';
import useVisibilityAwareInterval from 'src/hooks/useVisibilityAwareInterval';

const NavItemStatusContext = React.createContext({
  statuses: {},
  loading: false,
  refresh: () => Promise.resolve(),
});

export function useNavItemStatuses() {
  return React.useContext(NavItemStatusContext);
}

export function NavItemStatusProvider({ resolvers = [], refreshIntervalMs = 30 * 60 * 1000, dependencies = [], children }) {
  const [statuses, setStatuses] = React.useState({});
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!resolvers || resolvers.length === 0) {
      setStatuses({});
      return;
    }

    setLoading(true);

    try {
      const results = await Promise.all(
        resolvers.map(async ({ key, resolve }) => {
          if (!key || typeof resolve !== 'function') return null;
          try {
            const status = await resolve();
            return status ? [key, status] : null;
          } catch (e) {
            // If a resolver fails, surface an error status for that key
            return [key, { type: 'error', tooltip: 'Unable to load status', blocked: false }];
          }
        }),
      );

      const entries = results.filter(Boolean);
      setStatuses(Object.fromEntries(entries));
    } finally {
      setLoading(false);
    }
  }, [resolvers]);

  React.useEffect(() => {
    refresh();
    // We intentionally want to re-run when dependencies change (e.g., selected client)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, ...dependencies]);

  // Keep the refresh running even if the tab is backgrounded; these status checks are cheap and important.
  useVisibilityAwareInterval(refresh, refreshIntervalMs, {
    pauseOnHidden: false,
    runImmediatelyOnVisible: false,
  });

  const value = React.useMemo(
    () => ({
      statuses,
      loading,
      refresh,
    }),
    [statuses, loading, refresh],
  );

  return <NavItemStatusContext.Provider value={value}>{children}</NavItemStatusContext.Provider>;
}

NavItemStatusProvider.propTypes = {
  resolvers: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      resolve: PropTypes.func.isRequired,
    }),
  ),
  refreshIntervalMs: PropTypes.number,
  dependencies: PropTypes.array,
  children: PropTypes.node,
};

export default NavItemStatusContext;
